import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as actions from '../../../../../SalesPage/actions';
import { getFromLocalStorage } from '../../../../../../global/helpers';
import * as Styled from './WoltProposalSearchModal.styles';

interface WoltProposalProduct {
  sku: string;
  name: string;
  count_desired: number;
  pharmacy_count: number;
  min_customer_price: number;
  quantity: number;
  count?: number;
  currentPrice?: number;
}

interface WoltProposalSearchModalProps {
  onClose: () => void;
  onConfirm: () => void;
  productName: string;
  originalTotalPrice: number;
  selectedProposal: WoltProposalProduct | null;
  onSelect: (product: any, qty: number) => void;
  onRemove: () => void;
  sourceCode: string;
  isWolt?: boolean;
}

const WoltProposalSearchModal = ({
  onClose,
  onConfirm,
  productName,
  originalTotalPrice,
  selectedProposal,
  onSelect,
  onRemove,
  sourceCode,
  isWolt,
}: WoltProposalSearchModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [searchValue, setSearchValue] = useState<string>('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCancel = () => {
    onRemove();
    setSearchValue('');
    setSearchResults([]);
    onClose();
  };

  const handleSearch = () => {
    if (!searchValue.trim() || searchValue.trim().length < 3) return;
    setIsLoading(true);
    dispatch(
      actions.getNewSearchProducts.request({
        searchValue,
        sourceCode,
        city: getFromLocalStorage('city') ?? 'Алматы',
        isWolt,
        postEffect: (response: any) => {
          const inStockProducts = response.matching_products.filter(
            (el: { in_stock: boolean }) => el.in_stock
          );
          setSearchResults(inStockProducts);
          setIsLoading(false);
        },
      })
    );
  };

  const handleInputChange = (value: string) => {
    setSearchValue(value);
    if (!value.trim()) {
      setSearchResults([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  const renderCardActions = (product: any) => {
    const isSelected = selectedProposal?.sku === product.sku;
    const maxQtyByStock = product.quantity;
    const maxQtyByPrice = Math.floor(
      originalTotalPrice / product.min_customer_price
    );
    const maxQty = Math.min(maxQtyByStock, maxQtyByPrice);

    if (isSelected) {
      return (
        <Styled.CounterRow>
          <Styled.CounterTotal>
            1 шт × {product.min_customer_price} ₸ = {product.min_customer_price}{' '}
            ₸
          </Styled.CounterTotal>
          <Styled.InlineRemoveButton onClick={onRemove}>
            {t('OrdersPage.WoltSearchModal.WOLT_PROPOSAL_REMOVE')}
          </Styled.InlineRemoveButton>
        </Styled.CounterRow>
      );
    }

    return (
      <Styled.SelectButton
        disabled={maxQty <= 0}
        onClick={() => onSelect(product, 1)}
      >
        {t(
          `OrdersPage.WoltSearchModal.${
            maxQty <= 0 ? 'WOLT_PROPOSAL_OVER_BUDGET' : 'WOLT_PROPOSAL_SELECT'
          }`
        )}
      </Styled.SelectButton>
    );
  };

  return (
    <Styled.Overlay
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <Styled.Modal>
        <Styled.Header>
          <span className="title">
            {t('OrdersPage.WoltSearchModal.WOLT_PROPOSAL_SEARCH_TITLE')}
          </span>
          <Styled.CloseButton onClick={handleCancel}>✕</Styled.CloseButton>
        </Styled.Header>

        <Styled.ProductInfo>
          {t('OrdersPage.WoltSearchModal.WOLT_PROPOSAL_PRODUCT')}{' '}
          <strong>{productName}</strong>
          {' | '}
          {t('OrdersPage.WoltSearchModal.WOLT_PROPOSAL_BUDGET')}{' '}
          <strong>{originalTotalPrice} ₸</strong>
        </Styled.ProductInfo>

        {selectedProposal && (
          <Styled.CurrentProposal>
            <div className="proposal-info">
              <span className="proposal-name">
                {t(
                  'OrdersPage.WoltSearchModal.WOLT_PROPOSAL_CURRENT_REPLACEMENT'
                )}
              </span>
              <span>{selectedProposal.name || selectedProposal.sku}</span>
              <span className="proposal-price">
                1 шт ×{' '}
                {selectedProposal.min_customer_price ??
                  selectedProposal.currentPrice ??
                  0}{' '}
                ₸{' = '}
                {selectedProposal.min_customer_price ??
                  selectedProposal.currentPrice ??
                  0}{' '}
                ₸
              </span>
            </div>
            <Styled.RemoveButton onClick={onRemove}>
              {t('OrdersPage.WoltSearchModal.WOLT_PROPOSAL_DELETE')}
            </Styled.RemoveButton>
          </Styled.CurrentProposal>
        )}

        <Styled.SearchInput
          type="text"
          placeholder={t(
            'OrdersPage.WoltSearchModal.WOLT_PROPOSAL_SEARCH_PLACEHOLDER'
          )}
          value={searchValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Styled.SearchButton
          disabled={searchValue.trim().length < 3}
          onClick={handleSearch}
        >
          {t('OrdersPage.WoltSearchModal.WOLT_PROPOSAL_SEARCH_BUTTON')}
        </Styled.SearchButton>

        {isLoading && (
          <Styled.LoadingText>
            {t('OrdersPage.WoltSearchModal.WOLT_PROPOSAL_LOADING')}
          </Styled.LoadingText>
        )}

        <Styled.ResultsList>
          {searchResults.map((product) => {
            const isSelected = selectedProposal?.sku === product.sku;
            const otherSelected = selectedProposal !== null && !isSelected;

            return (
              <Styled.ProductCard
                key={product.sku}
                isSelected={isSelected}
                isDisabled={otherSelected}
              >
                <Styled.ProductImage
                  src={`https://db-images.object.pscloud.io/optimized_v4_img_small_${product.sku}.webp`}
                  alt={product.name}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <Styled.ProductDetails>
                  <div className="product-name">{product.name}</div>
                  <div className="product-meta">
                    {product.manufacturer} · {product.pp_packing}
                  </div>
                  <div className="product-price">
                    {product.min_customer_price} ₸
                    <span className="product-stock">
                      {t('OrdersPage.WoltSearchModal.WOLT_PROPOSAL_IN_STOCK')}{' '}
                      {product.quantity} шт
                    </span>
                  </div>
                  {!otherSelected && renderCardActions(product)}
                </Styled.ProductDetails>
              </Styled.ProductCard>
            );
          })}
        </Styled.ResultsList>

        {!isLoading && searchValue && searchResults.length === 0 && (
          <Styled.EmptyText>
            {t('OrdersPage.WoltSearchModal.WOLT_PROPOSAL_EMPTY')}
          </Styled.EmptyText>
        )}

        {selectedProposal && (
          <Styled.ConfirmButton onClick={onConfirm}>
            {t('OrdersPage.WoltSearchModal.WOLT_PROPOSAL_DONE')}
          </Styled.ConfirmButton>
        )}
      </Styled.Modal>
    </Styled.Overlay>
  );
};

export default WoltProposalSearchModal;
